import React, { useState, useEffect, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import { getCreditPackages, createCheckoutSession } from '../services/apiService';
import CreditService from '../services/creditService';
import './CreditPurchaseModal.css';

export default function CreditPurchaseModal({ 
  isOpen, 
  onClose, 
  currentCredits = 0, 
  onPurchaseSuccess,
  initialPackages = null,
}) {
  const [packages, setPackages] = useState(initialPackages || []);
  const [loading, setLoading] = useState(!initialPackages);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const ctaSectionRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // Reset processing state when modal opens (in case user cancelled payment and came back)
      setProcessing(false);
      setError(null);

      // Check for cancelled payment in URL params
      const urlParams = new URLSearchParams(window.location.search);
      const paymentStatus = urlParams.get('payment');

      if (paymentStatus === 'cancelled') {
        // Clean up URL params
        window.history.replaceState({}, document.title, window.location.pathname);
        // Optionally show a message that payment was cancelled
        // setError('Payment was cancelled. You can try again when ready.');
      }

      // If we already have prefetched packages, use them immediately
      if (initialPackages && initialPackages.length > 0) {
        setPackages(initialPackages);
        // Auto-select the best value package (10 credits) if none selected yet
        const popularPackage = initialPackages.find(pkg => pkg.credits === 10) || initialPackages[initialPackages.length - 1];
        if (popularPackage) {
          setSelectedPackage(popularPackage);
        }
        setLoading(false);
      } else {
        loadPackages();
      }
    } else {
      // Reset state when modal closes
      setProcessing(false);
      setError(null);
      // Keep packages cached so reopening is instant
    }
  }, [isOpen, initialPackages]);

  const loadPackages = async () => {
    try {
      setLoading(true);
      setError(null);
      const packagesData = await getCreditPackages();
      setPackages(packagesData);
      // Auto-select the best value package (10 credits)
      const popularPackage = packagesData.find(pkg => pkg.credits === 10) || packagesData[packagesData.length - 1];
      if (popularPackage) {
        setSelectedPackage(popularPackage);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error loading credit packages:', err);
      setError('Failed to load credit packages. Please try again.');
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!selectedPackage) {
      setError('Please select a credit package');
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Create checkout session
      const checkoutData = await createCheckoutSession(
        selectedPackage.id,
        user.uid,
        user.email
      );

      // Redirect to Stripe Checkout
      if (checkoutData.url) {
        window.location.href = checkoutData.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError(err.message || 'Failed to start checkout. Please try again.');
      setProcessing(false);
    }
  };

  const calculateSavings = (pkg) => {
    if (pkg.credits < 10) return null;
    const basePrice = packages.find(p => p.credits === 1)?.price || 295;
    const originalPrice = basePrice * pkg.credits;
    const savings = originalPrice - pkg.price;
    return savings;
  };

  const scrollToPaymentButton = () => {
    if (ctaSectionRef.current) {
      setTimeout(() => {
        ctaSectionRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest' 
        });
      }, 100);
    }
  };

  const handlePackageSelect = (pkg) => {
    setSelectedPackage(pkg);
    setError(null);
    scrollToPaymentButton();
  };

  const getPackageBadge = (pkg) => {
    if (pkg.credits === 10) return { text: 'Best Value', type: 'best-value' };
    if (pkg.credits === 5) return { text: 'Most Popular', type: 'popular' };
    return null;
  };

  if (!isOpen) return null;

  return (
    <div className="credit-purchase-overlay-saas">
      <div className="credit-purchase-modal-saas">
        <button className="credit-purchase-close-btn-saas" onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        
        <div className="credit-purchase-modal-content-saas">
          <div className="credit-purchase-header-saas">
            <h2>Upgrade Your Credits</h2>
            <p className="credit-purchase-subtitle-saas">
              Unlock unlimited creator assignments. Credits never expire.
            </p>
          </div>

        {loading ? (
          <div className="credit-purchase-loading-saas">
            <div className="spinner-saas"></div>
            <p>Loading packages...</p>
          </div>
        ) : error && !packages.length ? (
          <div className="credit-purchase-error-saas">
            <p>{error}</p>
            <button className="btn-saas btn-primary-saas" onClick={loadPackages}>Retry</button>
          </div>
        ) : (
          <>
            {/* Package Grid */}
            <div className="credit-packages-grid-saas">
              {packages.map((pkg) => {
                const isSelected = selectedPackage?.id === pkg.id;
                const hasDiscount = pkg.credits >= 10;
                const savings = calculateSavings(pkg);
                const badge = getPackageBadge(pkg);
                
                return (
                  <div
                    key={pkg.id}
                    className={`credit-package-card-saas ${isSelected ? 'selected-saas' : ''} ${hasDiscount ? 'has-discount-saas' : ''} ${badge?.type === 'popular' ? 'popular-saas' : ''}`}
                    onClick={() => handlePackageSelect(pkg)}
                  >
                    {badge && (
                      <div className={`package-badge-saas badge-${badge.type}-saas`}>
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                          <path d="M8 0L10.5 5.5L16 8L10.5 10.5L8 16L5.5 10.5L0 8L5.5 5.5L8 0Z" fill="currentColor"/>
                        </svg>
                        {badge.text}
                      </div>
                    )}
                    
                    {hasDiscount && savings && (
                      <div className="package-savings-saas">
                        <span className="savings-icon-saas">💰</span>
                        Save £{savings.toFixed(2)}
                      </div>
                    )}

                    <div className="package-header-saas">
                      <div className="package-credits-saas">{pkg.credits}</div>
                      <div className="package-label-saas">Credits</div>
                    </div>

                    <div className="package-pricing-saas">
                      <div className="package-price-main-saas">
                        {pkg.priceFormatted}
                        <span className="package-currency-label-saas"> GBP</span>
                      </div>
                      <div className="package-price-per-credit-saas">
                        £{pkg.pricePerCredit.toFixed(2)} per credit
                      </div>
                      {pkg.credits === 5 && (
                        <div className="package-popular-note-saas" style={{ marginTop: '8px' }}>
                          Ideal for ongoing campaigns
                        </div>
                      )}
                      {hasDiscount && (
                        <div className="package-discount-note-saas">
                          <span className="discount-icon-saas">🎉</span>
                          5% discount applied
                        </div>
                      )}
                    </div>

                    <div className="package-features-saas">
                      {pkg.credits === 1 ? (
                        <>
                          <div className="feature-item-saas">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            <span>Use 1 credit for 1 Pro Creator or 2 Rising Stars</span>
                          </div>
                          <div className="feature-item-saas">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            <span>Great to get started and test</span>
                          </div>
                        </>
                      ) : pkg.credits === 5 ? (
                        <>
                          <div className="feature-item-saas">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            <span>5 pros / 10 rising stars</span>
                          </div>
                          <div className="feature-item-saas">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            <span>Great for building a library of UGC</span>
                          </div>
                        </>
                      ) : pkg.credits === 10 ? (
                        <>
                          <div className="feature-item-saas">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            <span>10 pro creators / 20 rising stars <span style={{color: '#10b981', fontWeight: '600'}}>(the 20th is on us)</span></span>
                          </div>
                          <div className="feature-item-saas">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            <span>Great for large content plans</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="feature-item-saas">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            <span>Never expires</span>
                          </div>
                          <div className="feature-item-saas">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            <span>Instant access</span>
                          </div>
                        </>
                      )}
                    </div>

                    {isSelected && (
                      <div className="package-selected-indicator-saas">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        Selected
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {error && (
              <div className="credit-purchase-error-message-saas">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                {error}
              </div>
            )}

            {/* Trust Signals */}
            <div className="trust-signals-saas">
              <div className="trust-item-saas">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                </svg>
                <span>Secure payment</span>
              </div>
              <div className="trust-item-saas">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                <span>You'll receive your credits instantly after payment.</span>
              </div>
              <div className="trust-item-saas">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
                <span>No expiration</span>
              </div>
            </div>

            {/* CTA Section */}
            <div className="credit-purchase-cta-saas" ref={ctaSectionRef}>
              {selectedPackage && (
                <div className="selected-package-summary-saas">
                  <div className="summary-item-saas">
                    <span className="summary-label-saas">You'll have:</span>
                    <span className="summary-value-saas summary-total-saas">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="summary-credit-icon-saas">
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M12 6v6l4 2"></path>
                      </svg>
                      {(currentCredits + selectedPackage.credits).toFixed(2)} credits
                    </span>
                  </div>
                  <div className="summary-item-saas">
                    <span className="summary-label-saas">Total:</span>
                    <span className="summary-value-saas summary-total-saas">{selectedPackage.priceFormatted} GBP</span>
                  </div>
                </div>
              )}

              <div className="credit-purchase-actions-saas">
                <button 
                  className="btn-saas btn-primary-saas btn-cta-saas" 
                  onClick={handlePurchase}
                  disabled={!selectedPackage || processing}
                >
                  {processing ? (
                    <>
                      <span className="btn-spinner-saas"></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      Continue to Payment
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                        <polyline points="12 5 19 12 12 19"></polyline>
                      </svg>
                    </>
                  )}
                </button>
                <div className="payment-urgency-message-saas">
                  Credits never expire — perfect to stock up ahead of your next campaign.
                </div>
              </div>
            </div>
          </>
        )}
        </div>
      </div>
    </div>
  );
}
